import React from 'react';
import {
  ServiceCategory,
  ServiceCategoryId,
} from '../constants';
import styles from '../Services.module.css';

interface ServicesHeroProps {
  categories: ServiceCategory[];
  searchQuery: string;
  selectedCategory: ServiceCategoryId;
  onSearchQueryChange: (value: string) => void;
  onSelectCategory: (categoryId: ServiceCategoryId) => void;
}

const getCategoryLabel = (category: ServiceCategory) => (
  category.id === 'all' ? 'All Categories' : category.name
);

const iconUrl = (fileName: string) => `${process.env.PUBLIC_URL}/icons/${fileName}`;
const iconMaskStyle = (fileName: string) => ({
  '--services-search-icon-url': `url("${iconUrl(fileName)}")`,
}) as React.CSSProperties;

const ChevronDownIcon: React.FC = () => (
  <svg className={styles.searchCategoryChevron} viewBox="0 0 20 20" aria-hidden="true">
    <path
      d="m5 7.5 5 5 5-5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const ServicesHero: React.FC<ServicesHeroProps> = ({
  categories,
  searchQuery,
  selectedCategory,
  onSearchQueryChange,
  onSelectCategory,
}) => {
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = React.useState(false);
  const [categoryMenuPlacement, setCategoryMenuPlacement] = React.useState<'bottom' | 'top'>('bottom');
  const [categoryMenuMaxHeight, setCategoryMenuMaxHeight] = React.useState<number | null>(null);
  const categoryControlRef = React.useRef<HTMLDivElement>(null);
  const categoryMenuRef = React.useRef<HTMLDivElement>(null);
  const categoryMenuId = React.useId();
  const selectedCategoryLabel = getCategoryLabel(
    categories.find((category) => category.id === selectedCategory) || categories[0]
  );
  const updateCategoryMenuLayout = React.useCallback(() => {
    const controlElement = categoryControlRef.current;

    if (!controlElement) {
      return;
    }

    const controlRect = controlElement.getBoundingClientRect();
    const menuGap = 6;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const availableBelow = viewportHeight - controlRect.bottom - menuGap;
    const availableAbove = controlRect.top - menuGap;
    const expectedMenuHeight = categoryMenuRef.current?.scrollHeight || categories.length * 44 + 12;
    const shouldOpenAbove = availableBelow < expectedMenuHeight && availableAbove > availableBelow;
    const availableSpace = shouldOpenAbove ? availableAbove : availableBelow;

    setCategoryMenuPlacement(shouldOpenAbove ? 'top' : 'bottom');
    setCategoryMenuMaxHeight(Math.max(120, Math.floor(Math.min(expectedMenuHeight, availableSpace))));
  }, [categories.length]);

  React.useLayoutEffect(() => {
    if (!isCategoryMenuOpen) {
      setCategoryMenuPlacement('bottom');
      setCategoryMenuMaxHeight(null);
      return undefined;
    }

    updateCategoryMenuLayout();

    window.addEventListener('resize', updateCategoryMenuLayout);
    window.addEventListener('scroll', updateCategoryMenuLayout, true);

    return () => {
      window.removeEventListener('resize', updateCategoryMenuLayout);
      window.removeEventListener('scroll', updateCategoryMenuLayout, true);
    };
  }, [isCategoryMenuOpen, updateCategoryMenuLayout]);

  React.useEffect(() => {
    if (!isCategoryMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (
        categoryControlRef.current &&
        !categoryControlRef.current.contains(event.target as Node)
      ) {
        setIsCategoryMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsCategoryMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCategoryMenuOpen]);

  const handleCategorySelect = (categoryId: ServiceCategoryId) => {
    onSelectCategory(categoryId);
    setIsCategoryMenuOpen(false);
  };

  return (
    <div className={`${styles.hero} ${isCategoryMenuOpen ? styles.heroMenuOpen : ''}`.trim()}>
      <div className={`container ${styles.heroInner}`}>
        <h1 className={styles.heroTitle}>
          <span>Find Services, Scholarships, and Recruiting Support</span>
          <span
            className={styles.heroTitleIcon}
            style={iconMaskStyle('service-fill.svg')}
            aria-hidden="true"
          />
        </h1>
        <p className={styles.heroDescription}>
          Explore profile-based services plus college and university opportunities shared for students
        </p>

        <div className={styles.searchWrap}>
          <label className={styles.searchField}>
            <span
              className={styles.searchIcon}
              style={iconMaskStyle('search-line.svg')}
              aria-hidden="true"
            />
            <span className={styles.searchSrOnly}>Search services</span>
            <input
              type="search"
              placeholder="Search coaching, photography, scholarships, recruiting, admissions..."
              value={searchQuery}
              onChange={(event) => onSearchQueryChange(event.target.value)}
              className={styles.searchInput}
            />
          </label>
          <span className={styles.searchDivider} aria-hidden="true" />
          <div className={styles.searchCategoryControl} ref={categoryControlRef}>
            <button
              type="button"
              className={styles.searchCategoryButton}
              aria-haspopup="listbox"
              aria-expanded={isCategoryMenuOpen}
              aria-controls={categoryMenuId}
              onClick={() => setIsCategoryMenuOpen((isOpen) => !isOpen)}
            >
              <span
                className={styles.searchCategoryIcon}
                style={iconMaskStyle('category-fill.svg')}
                aria-hidden="true"
              />
              <span className={styles.searchCategoryButtonText}>{selectedCategoryLabel}</span>
              <ChevronDownIcon />
            </button>
            {isCategoryMenuOpen ? (
              <div
                id={categoryMenuId}
                ref={categoryMenuRef}
                className={`${styles.searchCategoryMenu} ${categoryMenuPlacement === 'top' ? styles.searchCategoryMenuAbove : ''}`.trim()}
                style={categoryMenuMaxHeight ? {
                  '--services-category-menu-max-height': `${categoryMenuMaxHeight}px`,
                } as React.CSSProperties : undefined}
                role="listbox"
                aria-label="Service category"
              >
                {categories.map((category) => {
                  const isSelected = selectedCategory === category.id;

                  return (
                    <button
                      key={category.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`${styles.searchCategoryOption} ${isSelected ? styles.searchCategoryOptionActive : ''}`.trim()}
                      onClick={() => handleCategorySelect(category.id)}
                    >
                      <span>{getCategoryLabel(category)}</span>
                      {isSelected ? <span className={styles.searchCategoryCheck} aria-hidden="true" /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ServicesHero);
